import { useEffect, useState } from "react";
import { Calculator, Truck, Loader2, BadgeCheck, Globe2, Plane, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../lib/api";
import CountrySearchSelect from "./CountrySearchSelect";
import { Phone } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm outline-none transition focus:border-saffron focus:ring-2 focus:ring-saffron/20";

function RateCards({ result, onSelect }) {
  return (
    <div className="mt-6" data-testid="rate-results">
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
        <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-600">
          Chargeable weight: <span className="font-mono">{result.chargeable_weight} kg</span>
        </span>
        {result.zone && (
          <span className="rounded-full bg-blue-50 px-3 py-1 font-bold text-express">Zone {result.zone}</span>
        )}
        <span className={`rounded-full px-3 py-1 font-semibold ${result.source === "live" ? "bg-emerald-50 text-emerald-700" : result.source === "indiafast-intl-rates" ? "bg-blue-50 text-express" : "bg-amber-50 text-amber-700"}`}>
          {result.source === "live" ? `Live partner rates · ${result.rates.length} couriers` : result.source === "indiafast-intl-rates" ? "INDIAFAST international rate engine" : "Estimated network rates"}
        </span>
        <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
          Inclusive of 18% GST
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {result.rates.map((r, i) => (
          <div key={i} data-testid={`rate-display-card-${i}`}
               className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-saffron hover:shadow-lg hover:shadow-saffron/10">
            <div className="absolute inset-x-0 top-0 h-0.5 animate-beam bg-gradient-to-r from-transparent via-saffron to-transparent" />
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                {r.image ? (
                  <img src={r.image} alt="" loading="lazy" className="h-8 w-8 shrink-0 rounded-md bg-slate-50 object-contain"
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  r.is_international ? <Plane className="h-4 w-4 shrink-0 text-express" /> : <Truck className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <span className="truncate font-heading text-sm font-bold text-navy">{r.courier_name}</span>
              </div>
              {i === 0 && <span className="flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><BadgeCheck className="h-3 w-3" /> BEST</span>}
            </div>
            <p className="mt-1 text-xs text-slate-500">{r.service}{r.edd ? ` · ${r.edd}` : ""}{r.zone ? ` · Zone ${r.zone}` : ""}{r.notes ? ` · ${r.notes}` : ""}</p>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <p className="font-heading text-2xl font-extrabold text-navy">₹{r.customer_price.toLocaleString("en-IN")}</p>
                <p className="text-[11px] text-slate-400">incl. 18% GST · all-inclusive</p>
              </div>
              {onSelect && (
                <button type="button" onClick={() => onSelect(r)} data-testid={`rate-select-btn-${i}`}
                        className="rounded-full bg-saffron px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-saffron-hover">
                  Select
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function RateCalculator({ onSelect, onModeChange, initialMode = "domestic", initialCountry = "", initialWeight = "" }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({
    pickup_pincode: "", delivery_pincode: "", weight: "",
    length: "", width: "", height: "", payment_type: "PREPAID",
  });
  const [intl, setIntl] = useState({ destination_country: initialCountry, weight: initialWeight, length: "", width: "", height: "", zipcode: "" });
  const [countries, setCountries] = useState([]);
  const [zoned, setZoned] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadCountries = (attempt = 0) => {
      api.get("/rates/countries")
        .then((r) => { if (!cancelled) { setCountries(r.data.countries); setZoned(r.data.zoned || {}); } })
        .catch(() => { if (!cancelled && attempt < 2) setTimeout(() => loadCountries(attempt + 1), 900); });
    };
    loadCountries();
    return () => { cancelled = true; };
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setResult(null);
    onModeChange?.(m);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setI = (k) => (e) => setIntl({ ...intl, [k]: e.target.value });

  const num = (v) => parseFloat(v) || 0;

  const calculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = mode === "domestic" ? {
        pickup_pincode: form.pickup_pincode,
        delivery_pincode: form.delivery_pincode,
        weight: num(form.weight) || 0.5,
        length: num(form.length), width: num(form.width), height: num(form.height),
        payment_type: form.payment_type,
      } : {
        destination_country: intl.destination_country,
        zipcode: intl.zipcode,
        weight: num(intl.weight) || 0.5,
        length: num(intl.length), width: num(intl.width), height: num(intl.height),
      };
      const res = await api.post(mode === "domestic" ? "/rates/calculate" : "/rates/international", payload);
      setResult(res.data);
      if (res.data.zone_required) toast.error(res.data.unknown_zipcode
        ? "This postcode is not mapped to a price zone yet — our team will confirm the exact price."
        : "Enter the delivery postcode to see zone-wise pricing.");
      else if (!res.data.rates.length) toast.error(mode === "international"
        ? "Price on request for this country — contact us at +91 97838 07838"
        : "No courier serviceable on this lane right now.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="rate-calculator" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60 sm:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-saffron-light text-saffron"><Calculator className="h-5 w-5" /></span>
          <div>
            <h3 className="font-heading text-xl font-bold text-navy">Instant Rate Calculator</h3>
            <p className="text-xs text-slate-500">Live multi-courier rates · transparent all-inclusive pricing</p>
          </div>
        </div>
        <div className="flex rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="rate-mode-tabs">
          <button type="button" onClick={() => switchMode("domestic")} data-testid="rate-tab-domestic"
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${mode === "domestic" ? "bg-saffron text-white shadow" : "text-slate-500 hover:text-navy"}`}>
            <Truck className="h-3.5 w-3.5" /> Domestic
          </button>
          <button type="button" onClick={() => switchMode("international")} data-testid="rate-tab-international"
                  className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${mode === "international" ? "bg-express text-white shadow" : "text-slate-500 hover:text-navy"}`}>
            <Globe2 className="h-3.5 w-3.5" /> International
          </button>
        </div>
      </div>

      {mode === "domestic" ? (
        <form onSubmit={calculate} className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Pickup Pincode</label>
            <input required data-testid="rate-calculator-origin-input" className={inputCls} placeholder="122001" maxLength={6}
                   value={form.pickup_pincode} onChange={set("pickup_pincode")} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery Pincode</label>
            <input required data-testid="rate-calculator-destination-input" className={inputCls} placeholder="400001" maxLength={6}
                   value={form.delivery_pincode} onChange={set("delivery_pincode")} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Weight (KG)</label>
            <input required type="number" step="0.1" min="0.1" data-testid="rate-calculator-weight-input" className={inputCls}
                   placeholder="1.0" value={form.weight} onChange={set("weight")} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</label>
            <select data-testid="rate-calculator-payment-select" className={inputCls} value={form.payment_type} onChange={set("payment_type")}>
              <option value="PREPAID">Prepaid</option>
              <option value="COD">COD</option>
            </select>
          </div>
          {["length", "width", "height"].map((d) => (
            <div key={d}>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{d[0].toUpperCase()} (cm) <span className="normal-case text-slate-400">opt.</span></label>
              <input type="number" min="0" className={inputCls} placeholder="—" value={form[d]} onChange={set(d)} />
            </div>
          ))}
          <div className="flex items-end">
            <button type="submit" disabled={loading} data-testid="rate-calculator-submit-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-saffron disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
              {loading ? "Calculating…" : "Get Domestic Rates"}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={calculate} className="grid grid-cols-2 gap-4 md:grid-cols-3">
          <div className="col-span-2 md:col-span-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Destination Country</label>
            <CountrySearchSelect countries={countries} value={intl.destination_country} inputClass={inputCls}
                                 onChange={(v) => setIntl({ ...intl, destination_country: v })}
                                 placeholder="Select country — type to search" testid="intl-country-select"
                                 extraOptions={[{ value: "__other", label: "Other country (Rest of World)" }]} />
          </div>
          {zoned[intl.destination_country] && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery Postcode / ZIP</label>
              <input data-testid="intl-zipcode-input" className={inputCls}
                     placeholder={intl.destination_country === "Canada" ? "e.g. M5V 1J1 (optional)" : "e.g. 2000 (optional)"}
                     value={intl.zipcode} onChange={setI("zipcode")} />
              <p className="mt-1 text-[10px] font-semibold text-express">Optional — enter postcode for your exact zone price; otherwise the lowest zone price is shown for all express services</p>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Weight (KG)</label>
            <input required type="number" step="0.1" min="0.1" data-testid="intl-weight-input" className={inputCls}
                   placeholder="1.0" value={intl.weight} onChange={setI("weight")} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["length", "width", "height"].map((d) => (
              <div key={d}>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{d[0].toUpperCase()}</label>
                <input type="number" min="0" className={inputCls} placeholder="cm" value={intl[d]} onChange={setI(d)} />
              </div>
            ))}
          </div>
          <div className="col-span-2 flex flex-wrap items-end gap-3 md:col-span-3">
            <button type="submit" disabled={loading} data-testid="intl-submit-btn"
                    className="flex items-center justify-center gap-2 rounded-lg bg-express px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-navy disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe2 className="h-4 w-4" />}
              {loading ? "Calculating…" : "Get International Rates"}
            </button>
            {intl.destination_country && intl.destination_country !== "__other" && (
              <a href={`${API_BASE}/api/rates/international/sheet.pdf?country=${encodeURIComponent(intl.destination_country)}`}
                 target="_blank" rel="noreferrer" data-testid="intl-download-rate-sheet-btn"
                 className="flex items-center gap-2 rounded-lg border border-express/40 bg-express/5 px-4 py-2.5 text-xs font-bold text-express transition-colors hover:bg-express hover:text-white">
                <FileDown className="h-4 w-4" /> Download Full Rate List (PDF)
              </a>
            )}
            <p className="text-xs text-slate-400">Zone-based slabs per 0.5 kg · fuel surcharge included · customs docs guided at booking</p>
          </div>
        </form>
      )}

      {intl.destination_country === "__other" && mode === "international" && (
        <input className={`${inputCls} mt-3`} placeholder="Type destination country" data-testid="intl-country-custom"
               onChange={(e) => setIntl({ ...intl, destination_country: e.target.value })} />
      )}

      {result && mode === "international" && result.price_on_request && !(result.rates || []).length && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="price-on-request">
          <p className="font-heading text-lg font-bold text-navy">
            Price on request — {result.chargeable_weight} kg to {result.destination_country}
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-slate-600">
            This weight is above our online slab rates for this destination. Call customer care for an exact bulk price.
          </p>
          <a href="tel:9783807838" data-testid="price-on-request-call-btn"
             className="mt-4 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
            <Phone className="h-4 w-4" /> Call +91 97838 07838
          </a>
        </div>
      )}
      {result?.zone_approx && (
        <p className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-semibold text-blue-700" data-testid="zone-approx-note">
          Showing lowest zone price — enter the delivery postcode for your exact zone rate.
        </p>
      )}
      {result && <RateCards result={result} onSelect={onSelect} />}
    </div>
  );
}
